'use client';
import React, { useState } from 'react';
import Badge from '@/components/ui/Badge';
import Icon from '@/components/ui/AppIcon';

type EventType = 'wildlife' | 'distress' | 'lost_equipment' | 'ice_shelf';
type EventStatus = 'Detected' | 'Classified' | 'Routed' | 'Acknowledged' | 'Resolved';

interface DetectionEvent {
  id: string;
  type: EventType;
  confidence: number;
  species?: string;
  freqHz: string;
  depthM: number;
  lat: number;
  lon: number;
  channel: string;
  timestamp: string;
  status: EventStatus;
}

const events: DetectionEvent[] = [
  { id: 'evt-8841', type: 'distress', confidence: 98.2, freqHz: '8.2k', depthM: 12, lat: -34.21, lon: 18.47, channel: '🔉+D🌊+📡', timestamp: '05:02:11', status: 'Routed' },
  { id: 'evt-8840', type: 'distress', confidence: 96.7, freqHz: '7.8k', depthM: 8, lat: -34.19, lon: 18.52, channel: '🔉+D🌊+📡', timestamp: '05:01:44', status: 'Classified' },
  { id: 'evt-8839', type: 'wildlife', confidence: 94.1, species: 'Humpback Whale', freqHz: '0.3k', depthM: 187, lat: -35.44, lon: 19.21, channel: '🔉+D🌊', timestamp: '05:01:03', status: 'Acknowledged' },
  { id: 'evt-8838', type: 'wildlife', confidence: 88.6, species: 'Bottlenose Dolphin', freqHz: '12k', depthM: 42, lat: -35.11, lon: 19.88, channel: '🔉', timestamp: '05:00:31', status: 'Resolved' },
  { id: 'evt-8837', type: 'lost_equipment', confidence: 79.3, species: 'Pinger BN-4412', freqHz: '37.5k', depthM: 2140, lat: -36.02, lon: 20.14, channel: 'D🌊+📡', timestamp: '04:59:58', status: 'Routed' },
  { id: 'evt-8836', type: 'wildlife', confidence: 91.8, species: 'Sperm Whale', freqHz: '2.1k', depthM: 340, lat: -35.77, lon: 18.94, channel: '🔉+D🌊', timestamp: '04:59:22', status: 'Acknowledged' },
  { id: 'evt-8835', type: 'ice_shelf', confidence: 85.4, species: 'Thwaites Calving', freqHz: '0.05k', depthM: 0, lat: -75.50, lon: -106.80, channel: '🔉+📡', timestamp: '04:58:44', status: 'Classified' },
  { id: 'evt-8834', type: 'wildlife', confidence: 77.2, species: 'Common Dolphin', freqHz: '18k', depthM: 28, lat: -34.88, lon: 19.43, channel: '🔉', timestamp: '04:58:01', status: 'Resolved' },
  { id: 'evt-8833', type: 'lost_equipment', confidence: 82.9, species: 'AUV-7 Beacon', freqHz: '25k', depthM: 880, lat: -36.14, lon: 20.77, channel: 'D🌊+📡', timestamp: '04:57:33', status: 'Acknowledged' },
  { id: 'evt-8832', type: 'wildlife', confidence: 96.3, species: 'Blue Whale', freqHz: '0.18k', depthM: 220, lat: -35.62, lon: 18.31, channel: '🔉+D🌊', timestamp: '04:57:01', status: 'Resolved' },
  { id: 'evt-8831', type: 'wildlife', confidence: 72.1, species: 'Orca', freqHz: '6k', depthM: 95, lat: -34.55, lon: 19.67, channel: '🔉', timestamp: '04:56:22', status: 'Resolved' },
  { id: 'evt-8830', type: 'ice_shelf', confidence: 88.7, species: 'Ross Shelf Crack', freqHz: '0.08k', depthM: 0, lat: -78.20, lon: -162.40, channel: '🔉+📡', timestamp: '04:55:49', status: 'Classified' },
];

const typeLabel: Record<EventType, string> = {
  wildlife: 'Wildlife',
  distress: 'Distress',
  lost_equipment: 'Equipment',
  ice_shelf: 'Ice Shelf',
};

const typeBadge: Record<EventType, 'wildlife' | 'distress' | 'equipment' | 'ice'> = {
  wildlife: 'wildlife',
  distress: 'distress',
  lost_equipment: 'equipment',
  ice_shelf: 'ice',
};

const statusBadge: Record<EventStatus, 'positive' | 'negative' | 'warning' | 'info' | 'neutral'> = {
  Detected: 'info',
  Classified: 'warning',
  Routed: 'info',
  Acknowledged: 'positive',
  Resolved: 'neutral',
};

type FilterType = 'all' | EventType;

export default function DetectionEventFeed() {
  const [filter, setFilter] = useState<FilterType>('all');
  const [search, setSearch] = useState('');

  const filtered = events.filter((e) => {
    const matchFilter = filter === 'all' || e.type === filter;
    const matchSearch = search === '' ||
      e.id.includes(search) ||
      (e.species?.toLowerCase().includes(search.toLowerCase())) ||
      e.type.includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  const filterTabs: { key: FilterType; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'distress', label: 'Distress' },
    { key: 'wildlife', label: 'Wildlife' },
    { key: 'lost_equipment', label: 'Equipment' },
    { key: 'ice_shelf', label: 'Ice Shelf' },
  ];

  return (
    <div className="bg-card border border-border rounded-xl flex flex-col" style={{ minHeight: 420 }}>
      <div className="px-5 py-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Live Detection Feed</h2>
          <p className="text-xs text-muted-foreground">Real-time acoustic events — auto-routing active</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Icon name="MagnifyingGlassIcon" size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search events..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 bg-muted border border-border rounded-lg text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring w-44"
            />
          </div>
          <div className="flex items-center gap-1">
            {filterTabs.map((tab) => (
              <button
                key={`filter-${tab.key}`}
                onClick={() => setFilter(tab.key)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all duration-150 ${
                  filter === tab.key
                    ? 'bg-primary/10 text-primary border border-primary/20' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto scrollbar-ocean flex-1">
        <table className="w-full text-xs min-w-[700px]">
          <thead>
            <tr className="border-b border-border">
              {['Event ID', 'Type', 'Confidence', 'Species / Target', 'Freq', 'Depth', 'Coordinates', 'Channel', 'Time', 'Status'].map((col) => (
                <th key={`th-${col}`} className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-muted-foreground whitespace-nowrap">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((ev) => (
              <tr
                key={ev.id}
                className="border-b border-border/50 hover:bg-muted/40 transition-colors duration-100 group"
              >
                <td className="px-4 py-3 font-mono text-primary">{ev.id}</td>
                <td className="px-4 py-3">
                  <Badge variant={typeBadge[ev.type]}>{typeLabel[ev.type]}</Badge>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-12 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${ev.confidence}%`,
                          background: ev.confidence > 90 ? 'var(--positive)' : ev.confidence > 75 ? 'var(--warning)' : 'var(--negative)',
                        }}
                      />
                    </div>
                    <span className="font-tabular text-foreground">{ev.confidence.toFixed(1)}%</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-foreground max-w-[140px] truncate">{ev.species || '—'}</td>
                <td className="px-4 py-3 font-mono text-accent">{ev.freqHz} Hz</td>
                <td className="px-4 py-3 font-tabular text-muted-foreground">{ev.depthM > 0 ? `${ev.depthM}m` : 'Surface'}</td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground whitespace-nowrap">
                  {ev.lat.toFixed(2)}, {ev.lon.toFixed(2)}
                </td>
                <td className="px-4 py-3 font-mono text-[10px] text-primary whitespace-nowrap">{ev.channel}</td>
                <td className="px-4 py-3 font-mono text-muted-foreground whitespace-nowrap">{ev.timestamp}</td>
                <td className="px-4 py-3">
                  <Badge variant={statusBadge[ev.status]} dot>{ev.status}</Badge>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={10} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <Icon name="FunnelIcon" size={24} className="text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">No detection events match your filters</p>
                    <button onClick={() => { setFilter('all'); setSearch(''); }} className="text-xs text-primary hover:underline">
                      Clear filters
                    </button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="px-5 py-3 border-t border-border flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Showing {filtered.length} of {events.length} events
        </p>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-positive status-dot-active" />
          <span className="text-xs text-muted-foreground">Live — updating every 3s</span>
        </div>
      </div>
    </div>
  );
}